import React, { useState, useEffect } from "react";

const PercentageChangeCalculator = () => {
  const [originalValue, setOriginalValue] = useState(100);
  const [newValue, setNewValue] = useState(150);
  const [calculationType, setCalculationType] = useState("change");
  const [percentageValue, setPercentageValue] = useState(50);
  const [baseValue, setBaseValue] = useState(100);
  const [resultValue, setResultValue] = useState(150);
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [showSteps, setShowSteps] = useState("yes");
  const [result, setResult] = useState({
    percentageChange: "0",
    category: "",
    interpretation: "",
    advice: "",
    absoluteChange: "",
    changeType: "",
  });

  // Percentage Change Calculation
  const calculatePercentageChange = () => {
    let original = parseFloat(originalValue);
    let newVal = parseFloat(newValue);
    let percent = parseFloat(percentageValue);
    let base = parseFloat(baseValue);
    let resultVal = parseFloat(resultValue);
    let decimals = parseInt(decimalPlaces);

    if (calculationType === "change") {
      // Calculate percentage change from original to new value
      if (isNaN(original) || isNaN(newVal) || original === 0) return;

      const absoluteChange = newVal - original;
      const percentageChange = ((newVal - original) / Math.abs(original)) * 100;

      // Determine category and interpretation
      let category = "";
      let interpretation = "";
      let changeType = percentageChange >= 0 ? "Increase" : "Decrease";

      const absPercentChange = Math.abs(percentageChange);

      if (absPercentChange === 0) {
        category = "No Change";
        interpretation = "The values are identical. There has been no change between the original and new value.";
      } else if (absPercentChange < 5) {
        category = "Minimal Change";
        interpretation = `A ${changeType.toLowerCase()} of less than 5% represents a minor fluctuation. This could be due to normal variance or slight adjustments.`;
      } else if (absPercentChange < 15) {
        category = "Moderate Change";
        interpretation = `A ${changeType.toLowerCase()} of ${absPercentChange.toFixed(decimals)}% indicates a noticeable shift. This suggests meaningful movement worth monitoring.`;
      } else if (absPercentChange < 30) {
        category = "Significant Change";
        interpretation = `A ${changeType.toLowerCase()} of ${absPercentChange.toFixed(decimals)}% represents a substantial change. This level of variation typically requires attention and analysis.`;
      } else if (absPercentChange < 50) {
        category = "Major Change";
        interpretation = `A ${changeType.toLowerCase()} of ${absPercentChange.toFixed(decimals)}% is a major shift. This magnitude of change often indicates significant events or transformations.`;
      } else if (absPercentChange < 100) {
        category = "Dramatic Change";
        interpretation = `A ${changeType.toLowerCase()} of ${absPercentChange.toFixed(decimals)}% is dramatic. This represents a fundamental shift that requires comprehensive analysis.`;
      } else {
        category = "Extreme Change";
        interpretation = `A ${changeType.toLowerCase()} of ${absPercentChange.toFixed(decimals)}% is extreme. This level of change indicates extraordinary circumstances or exponential growth/decline.`;
      }

      // Generate advice
      let advice = "";
      if (absPercentChange === 0) {
        advice = "No action needed. Values remain stable. Continue monitoring for any future changes.";
      } else if (absPercentChange < 10) {
        advice = "Minor changes are normal. Track trends over time to identify patterns. Consider if this aligns with expectations.";
      } else if (absPercentChange < 25) {
        advice = "Moderate changes warrant investigation. Identify contributing factors and assess whether intervention is needed. Document the drivers of this change.";
      } else if (absPercentChange < 50) {
        advice = "Significant changes require analysis and potentially strategic adjustments. Review underlying causes and develop action plans if needed.";
      } else {
        advice = "Major changes demand immediate attention. Conduct thorough root cause analysis, assess impact across all areas, and implement corrective or capitalizing strategies as appropriate.";
      }

      setResult({
        percentageChange: `${percentageChange.toFixed(decimals)}%`,
        category,
        interpretation,
        advice,
        absoluteChange: absoluteChange.toFixed(decimals),
        changeType,
        originalValue: original.toFixed(decimals),
        newValue: newVal.toFixed(decimals),
        multiplier: (newVal / original).toFixed(3),
      });

    } else if (calculationType === "increase") {
      // Calculate value after percentage increase
      if (isNaN(base) || isNaN(percent)) return;

      const increaseAmount = (base * percent) / 100;
      const finalValue = base + increaseAmount;

      setResult({
        percentageChange: `${percent.toFixed(decimals)}%`,
        category: "Increase Calculation",
        interpretation: `Adding ${percent}% to ${base} results in ${finalValue.toFixed(decimals)}. The increase amount is ${increaseAmount.toFixed(decimals)}.`,
        advice: "This shows what your value becomes after applying the percentage increase. Useful for calculating markups, raises, or growth projections.",
        absoluteChange: increaseAmount.toFixed(decimals),
        changeType: "Increase",
        originalValue: base.toFixed(decimals),
        newValue: finalValue.toFixed(decimals),
        multiplier: "1." + (percent.toString().padStart(3, '0')),
      });

    } else if (calculationType === "decrease") {
      // Calculate value after percentage decrease
      if (isNaN(base) || isNaN(percent)) return;

      const decreaseAmount = (base * percent) / 100;
      const finalValue = base - decreaseAmount;

      setResult({
        percentageChange: `${percent.toFixed(decimals)}%`,
        category: "Decrease Calculation",
        interpretation: `Subtracting ${percent}% from ${base} results in ${finalValue.toFixed(decimals)}. The decrease amount is ${decreaseAmount.toFixed(decimals)}.`,
        advice: "This shows what your value becomes after applying the percentage decrease. Useful for calculating discounts, reductions, or depreciation.",
        absoluteChange: decreaseAmount.toFixed(decimals),
        changeType: "Decrease",
        originalValue: base.toFixed(decimals),
        newValue: finalValue.toFixed(decimals),
        multiplier: "0." + ((100 - percent).toString().padStart(3, '0')),
      });

    } else if (calculationType === "reverse") {
      // Calculate original value before percentage change
      if (isNaN(resultVal) || isNaN(percent)) return;

      const originalCalc = resultVal / (1 + percent / 100);
      const change = resultVal - originalCalc;

      setResult({
        percentageChange: `${percent.toFixed(decimals)}%`,
        category: "Reverse Calculation",
        interpretation: `If ${resultVal} represents a ${percent}% increase, the original value was ${originalCalc.toFixed(decimals)}. The absolute change was ${change.toFixed(decimals)}.`,
        advice: "This reverse calculation helps determine the starting value when you know the final result and percentage change. Useful for back-calculating original prices or baseline metrics.",
        absoluteChange: change.toFixed(decimals),
        changeType: "Increase",
        originalValue: originalCalc.toFixed(decimals),
        newValue: resultVal.toFixed(decimals),
        multiplier: (1 + percent / 100).toFixed(3),
      });
    }
  };

  useEffect(() => {
    calculatePercentageChange();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </div>
          <h1>Percentage Change Calculator</h1>
          <p>
            Calculate percentage changes, increases, decreases, and reverse calculations. A versatile tool for analyzing growth rates, discounts, markups, and comparing values across any domain.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Calculation Details
              </h2>

              {/* Calculation Type */}
              <div className="input-group">
                <label>Calculation Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "change", label: "% Change" },
                    { value: "increase", label: "% Increase" },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? "primary" : "secondary"}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{ marginTop: "0.5rem" }}>
                  {[
                    { value: "decrease", label: "% Decrease" },
                    { value: "reverse", label: "Reverse Calc" },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? "primary" : "secondary"}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {calculationType === "change" && (
                <>
                  {/* Original Value Input */}
                  <div className="input-group">
                    <label>Original Value</label>
                    <div className="input-wrapper">
                      <span className="input-prefix">📊</span>
                      <input
                        type="number"
                        value={originalValue}
                        onChange={(e) => setOriginalValue(e.target.value)}
                        className="calc-input"
                        placeholder="100"
                      />
                    </div>
                    <div className="quick-select-group">
                      {[50, 100, 200, 500, 1000].map((val) => (
                        <button
                          key={val}
                          onClick={() => setOriginalValue(val)}
                          className="quick-select-btn primary"
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* New Value Input */}
                  <div className="input-group">
                    <label>New Value</label>
                    <div className="input-wrapper">
                      <span className="input-prefix">📈</span>
                      <input
                        type="number"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        className="calc-input"
                        placeholder="150"
                      />
                    </div>
                    <div className="quick-select-group">
                      {[75, 125, 150, 200, 250].map((val) => (
                        <button
                          key={val}
                          onClick={() => setNewValue(val)}
                          className="quick-select-btn primary"
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {(calculationType === "increase" || calculationType === "decrease") && (
                <>
                  {/* Base Value Input */}
                  <div className="input-group">
                    <label>Base Value</label>
                    <div className="input-wrapper">
                      <span className="input-prefix">💰</span>
                      <input
                        type="number"
                        value={baseValue}
                        onChange={(e) => setBaseValue(e.target.value)}
                        className="calc-input"
                        placeholder="100"
                      />
                    </div>
                    <div className="quick-select-group">
                      {[50, 100, 200, 500, 1000].map((val) => (
                        <button
                          key={val}
                          onClick={() => setBaseValue(val)}
                          className="quick-select-btn primary"
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Percentage Input */}
                  <div className="input-group">
                    <label>Percentage (%)</label>
                    <div className="input-wrapper">
                      <span className="input-prefix">%</span>
                      <input
                        type="number"
                        value={percentageValue}
                        onChange={(e) => setPercentageValue(e.target.value)}
                        className="calc-input"
                        placeholder="50"
                      />
                    </div>
                    <div className="quick-select-group">
                      {[10, 25, 50, 75, 100].map((val) => (
                        <button
                          key={val}
                          onClick={() => setPercentageValue(val)}
                          className="quick-select-btn success"
                        >
                          {val}%
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {calculationType === "reverse" && (
                <>
                  {/* Result Value Input */}
                  <div className="input-group">
                    <label>Final Value</label>
                    <div className="input-wrapper">
                      <span className="input-prefix">🎯</span>
                      <input
                        type="number"
                        value={resultValue}
                        onChange={(e) => setResultValue(e.target.value)}
                        className="calc-input"
                        placeholder="150"
                      />
                    </div>
                    <div className="quick-select-group">
                      {[75, 125, 150, 200, 250].map((val) => (
                        <button
                          key={val}
                          onClick={() => setResultValue(val)}
                          className="quick-select-btn primary"
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Percentage Input */}
                  <div className="input-group">
                    <label>Percentage Increase (%)</label>
                    <div className="input-wrapper">
                      <span className="input-prefix">%</span>
                      <input
                        type="number"
                        value={percentageValue}
                        onChange={(e) => setPercentageValue(e.target.value)}
                        className="calc-input"
                        placeholder="50"
                      />
                    </div>
                    <div className="quick-select-group">
                      {[10, 25, 50, 75, 100].map((val) => (
                        <button
                          key={val}
                          onClick={() => setPercentageValue(val)}
                          className="quick-select-btn success"
                        >
                          {val}%
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Decimal Places */}
              <div className="input-group">
                <label>Decimal Places</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🔢</span>
                  <input
                    type="number"
                    value={decimalPlaces}
                    onChange={(e) => setDecimalPlaces(e.target.value)}
                    className="calc-input"
                    placeholder="2"
                    min="0"
                    max="10"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 1, 2, 3, 4].map((val) => (
                    <button
                      key={val}
                      onClick={() => setDecimalPlaces(val)}
                      className="quick-select-btn success"
                    >
                      {val}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Number of decimal places in results.
                </p>
              </div>

              {/* Show Steps */}
              <div className="input-group">
                <label>Show Calculation Steps</label>
                <div className="quick-select-group">
                  {[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setShowSteps(option.value)}
                      className={`quick-select-btn ${showSteps === option.value ? "primary" : "secondary"}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculatePercentageChange} className="calculate-btn">
                Calculate Percentage
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Calculation Results
              </h2>

              <div className="result-card">
                {/* Percentage Change */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Percentage {result.changeType}</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.percentageChange}</div>
                  <div className="result-subtext">{result.category}</div>
                </div>

                {/* Values Breakdown */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Original Value</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="16"></line>
                          <line x1="8" y1="12" x2="16" y2="12"></line>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.originalValue}</div>
                    <div className="result-subtext">starting point</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">New Value</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                          <polyline points="17 6 23 6 23 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.newValue}</div>
                    <div className="result-subtext">ending point</div>
                  </div>
                </div>

                {/* Additional Metrics */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Absolute Change</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <polyline points="19 12 12 19 5 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.absoluteChange}</div>
                    <div className="result-subtext">difference amount</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Multiplier</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="9" x2="19" y2="9"></line>
                          <line x1="5" y1="15" x2="19" y2="15"></line>
                          <line x1="12" y1="5" x2="12" y2="3"></line>
                          <line x1="12" y1="21" x2="12" y2="19"></line>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>×{result.multiplier}</div>
                    <div className="result-subtext">growth factor</div>
                  </div>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">What This Means</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500", marginBottom: "0.75rem" }}>
                    {result.interpretation}
                  </p>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.advice}
                  </p>
                </div>

                {/* Change Categories */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">Change Magnitude Categories</h3>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#f3f4f6", borderRadius: "0.375rem", borderLeft: "4px solid #6b7280" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>No Change: 0%</div>
                    <div style={{ fontSize: "0.75rem", color: "#374151" }}>Values are identical</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dbeafe", borderRadius: "0.375rem", borderLeft: "4px solid #3b82f6" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Minimal: 0-5%</div>
                    <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Minor fluctuation</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#bfdbfe", borderRadius: "0.375rem", borderLeft: "4px solid #2563eb" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Moderate: 5-15%</div>
                    <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Noticeable shift</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fef3c7", borderRadius: "0.375rem", borderLeft: "4px solid #f59e0b" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Significant: 15-30%</div>
                    <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Substantial change</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fed7aa", borderRadius: "0.375rem", borderLeft: "4px solid #ea580c" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Major: 30-50%</div>
                    <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Large shift</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fecaca", borderRadius: "0.375rem", borderLeft: "4px solid #ef4444" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Dramatic: 50-100%</div>
                    <div style={{ fontSize: "0.75rem", color: "#991b1b" }}>Fundamental shift</div>
                  </div>
                  <div style={{ padding: "0.75rem", backgroundColor: "#fae8ff", borderRadius: "0.375rem", borderLeft: "4px solid #a855f7" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Extreme: 100%+</div>
                    <div style={{ fontSize: "0.75rem", color: "#581c87" }}>Extraordinary change</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Percentage Change</h3>
              <p>
                Percentage change is a mathematical concept used to express how much a value has increased or decreased relative to its original value. It's expressed as a percentage and provides a standardized way to compare changes across different scales and contexts.
                This metric is fundamental in finance, statistics, economics, and everyday calculations. Whether you're tracking stock prices, analyzing sales growth, calculating discounts, or measuring performance metrics, percentage change provides meaningful context.
                Understanding percentage calculations helps make informed decisions about investments, pricing strategies, budget allocations, and performance evaluations across business and personal finance scenarios.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Percentage Change Formulas:</h4>
            <div className="formula-display">
              Percentage Change = [(New Value - Original Value) / Original Value] × 100
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Value After Increase = Original Value × (1 + Percentage / 100)
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Value After Decrease = Original Value × (1 - Percentage / 100)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Original Value</div>
                <div className="formula-param-value">Starting point</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">New Value</div>
                <div className="formula-param-value">Ending point</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Change</div>
                <div className="formula-param-value">Difference amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Percentage</div>
                <div className="formula-param-value">Relative change</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What's the difference between percentage change and percentage point change?</h5>
              <p>
                Percentage change measures relative change (e.g., from 50% to 75% is a 50% increase), while percentage point change measures absolute difference (same example is a 25 percentage point increase). Use percentage change for rates of change and percentage points when discussing differences in percentages themselves.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can percentage change be negative?</h5>
              <p>
                Yes! A negative percentage change indicates a decrease. For example, if a value drops from 100 to 80, the percentage change is -20%. Some prefer to say "20% decrease" rather than "-20% change" for clarity.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why can't I calculate percentage change from zero?</h5>
              <p>
                Dividing by zero is mathematically undefined. If your original value is zero, you can't calculate a meaningful percentage change. Instead, report the absolute change or use a different baseline. Going from 0 to any positive number represents infinite growth.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate percentage change over multiple periods?</h5>
              <p>
                Don't add percentage changes! Use the compound formula: Total % Change = [(Final Value / Initial Value) - 1] × 100. For example, +10% followed by +10% doesn't equal +20%; it equals +21% because the second increase applies to the already-increased value.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's a good percentage change to aim for?</h5>
              <p>
                This depends entirely on context. In business, 10-20% annual revenue growth is often considered strong. For investments, 7-10% annually is historical stock market average. For discounts, 20-30% is attractive. Always compare to industry benchmarks and historical performance.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I reverse calculate from a percentage change?</h5>
              <p>
                If you know the final value and percentage increase, divide by (1 + percentage/100) to get the original. For example, if $150 represents a 50% increase, the original was $150 ÷ 1.50 = $100. For decreases, use (1 - percentage/100) instead.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PercentageChangeCalculator;